import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import { Add as AddIcon, Science as PipelineIcon } from '@mui/icons-material';
import { analysisFindingsAPI } from '../../services/api';

/**
 * Pipelines management — list of registered pipelines (#40).
 *
 * Table of registered pipelines with run + finding counts, filterable by
 * module and result type. Rows link to the per-pipeline detail page. The
 * catalog (result-types) supplies human labels for module + kind and the
 * filter options.
 */

const PipelinesManagement = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  useEffect(() => {
    analysisFindingsAPI.getResultTypes()
      .then((res) => setCatalog(res.data))
      .catch(() => setCatalog(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (moduleFilter) params.module = moduleFilter;
    if (kindFilter) params.kind = kindFilter;
    analysisFindingsAPI.getRegisteredPipelines(params)
      .then((res) => { if (!cancelled) { setPipelines(res.data.pipelines || []); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.msg || err.message || 'Failed to load pipelines'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [moduleFilter, kindFilter]);

  const moduleOptions = useMemo(() => (catalog ? Object.entries(catalog.modules) : []), [catalog]);
  const kindOptions = useMemo(() => (catalog ? Object.entries(catalog.result_types) : []), [catalog]);

  const kindLabel = (k) => catalog?.result_types?.[k]?.display_name || k;
  const moduleLabel = (m) => catalog?.modules?.[m]?.display_name || m;

  return (
    <Box sx={{ p: 3, maxWidth: 1300 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PipelineIcon color="primary" />
          <Typography variant="h5">Pipelines</Typography>
        </Stack>
        <Button
          component={RouterLink}
          to="/analysis/pipelines/register"
          variant="contained"
          startIcon={<AddIcon />}
        >
          Register pipeline
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Module</InputLabel>
          <Select label="Module" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
            <MenuItem value=""><em>All modules</em></MenuItem>
            {moduleOptions.map(([key, mod]) => (
              <MenuItem key={key} value={key}>{mod.display_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Result type</InputLabel>
          <Select label="Result type" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <MenuItem value=""><em>All result types</em></MenuItem>
            {kindOptions.map(([key, rt]) => (
              <MenuItem key={key} value={key}>{rt.display_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading pipelines…</Typography>
        </Stack>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && pipelines.length === 0 && (
        <Alert severity="info">
          No registered pipelines{(moduleFilter || kindFilter) ? ' match these filters' : ' yet'}.{' '}
          <MuiLink component={RouterLink} to="/analysis/pipelines/register">Register one</MuiLink>
        </Alert>
      )}

      {!loading && !error && pipelines.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pipeline</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Result type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Findings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pipelines.map((p) => (
                <TableRow
                  key={p.name}
                  hover
                  onClick={() => navigate(`/analysis/pipelines/${encodeURIComponent(p.name)}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                      {p.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{moduleLabel(p.module_name)}</TableCell>
                  <TableCell><Chip label={kindLabel(p.kind)} size="small" /></TableCell>
                  <TableCell>v{p.version}</TableCell>
                  <TableCell align="right">{Number(p.run_count).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(p.finding_count).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PipelinesManagement;
